<template>
  <div v-if="showPanel" class="px-4 pt-4 pb-3 border-b border-vice-light-grey">
    <div class="flex items-center justify-between mb-3">
      <span class="font-bold">Offline participation</span>
      <button
        type="button"
        data-cy="offline-participation-toggle"
        class="text-gray-500"
        @click="expanded = !expanded"
      >
        <PlusIcon v-if="!expanded" class="h-4 w-4" />
        <MinusIcon v-else class="h-4 w-4" />
      </button>
    </div>

    <div v-show="expanded">
      <div class="relative flex justify-center mb-3">
        <button
          type="button"
          data-cy="offline-participation-enlarge"
          class="absolute top-0 right-0 text-gray-500"
          title="Enlarge QR code"
        />
        <qrcode-vue
          class="cursor-pointer"
          :value="joinUrl"
          @click="qrModalOpen = true"
          :size="220"
          level="L"
          render-as="svg"
        />
      </div>

      <div class="text-center text-xs text-gray-500 mb-2">or visit</div>

      <button
        type="button"
        data-cy="offline-participation-link"
        class="w-full px-3 py-2 bg-vice-light-grey rounded-full text-sm text-center truncate"
        @click="copyLink"
      >
        {{ copied ? 'Copied!' : joinUrl }}
      </button>

      <p class="text-xxs text-gray-400 text-center mt-2">
        Students without a laptop can scan this code or open the link above to join chat &amp;
        reflection from their phone.
      </p>
    </div>

    <TransitionRoot as="template" :show="qrModalOpen">
      <Dialog as="div" class="fixed z-50 inset-0 overflow-y-auto" @close="qrModalOpen = false">
        <div class="flex items-center justify-center min-h-screen p-4 text-center">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0"
            enter-to="opacity-100"
            leave="ease-in duration-200"
            leave-from="opacity-100"
            leave-to="opacity-0"
          >
            <DialogOverlay class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </TransitionChild>

          <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
            &#8203;
          </span>
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div
              class="
                relative
                inline-block
                align-middle
                bg-white
                rounded-lg
                shadow-xl
                transform
                transition-all
                p-8
              "
            >
              <button
                type="button"
                data-cy="offline-participation-modal-close"
                class="absolute top-2 right-2 text-gray-500"
                @click="qrModalOpen = false"
              >
                <XMarkIcon class="h-6 w-6" />
              </button>

              <qrcode-vue :value="joinUrl" :size="qrModalSize" level="M" render-as="svg" />

              <p class="text-lg text-gray-700 text-center mt-4 max-w-xs mx-auto">
                Scan this code or visit
                <span class="font-medium text-gray-700">{{ joinUrl }}</span>
                to join chat &amp; reflection from your phone.
              </p>
            </div>
          </TransitionChild>
        </div>
      </Dialog>
    </TransitionRoot>
  </div>
</template>

<script>
import QrcodeVue from 'qrcode.vue';
import { MinusIcon, PlusIcon } from '@heroicons/vue/solid';
import { Dialog, DialogOverlay, TransitionChild, TransitionRoot } from '@headlessui/vue';
import useSessionStore from '@/Stores/useSessionStore';
import XMarkIcon from '@/Components/Icons/XMarkIcon.vue';

export default {
  components: {
    QrcodeVue,
    PlusIcon,
    MinusIcon,
    Dialog,
    DialogOverlay,
    TransitionChild,
    TransitionRoot,
    XMarkIcon,
  },
  data() {
    return {
      expanded: true,
      copied: false,
      qrModalOpen: false,
      qrModalSize: Math.min(560, typeof window !== 'undefined' ? window.innerWidth - 80 : 560),
    };
  },
  setup() {
    const sessionStore = useSessionStore();
    return { sessionStore };
  },
  computed: {
    session() {
      return this.$page.props.session;
    },
    joinUrl() {
      return this.$page.props.offlineParticipationJoinUrl || '';
    },
    showPanel() {
      return (
        this.sessionStore.isFacilitator &&
        !this.sessionStore.userIsObserver &&
        this.session?.details?.session_type === 'in_classroom' &&
        !!this.joinUrl
      );
    },
  },
  methods: {
    async copyLink() {
      try {
        await navigator.clipboard.writeText(this.joinUrl);
        this.copied = true;
        setTimeout(() => {
          this.copied = false;
        }, 2000);
      } catch (e) {
        console.error('Cannot copy');
      }
    },
  },
};
</script>
